<!-- app/javascript/dashboard/modules/tasks/components/SmartTitleInput.vue -->
<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import {
  useSmartTextParser,
  detectPendingToken,
  confirmToken,
  removeLastChip,
  derivePendingFields,
  extractTitleAndDescription,
} from '../composables/useSmartTextParser';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Título da tarefa' },
  autofocus: { type: Boolean, default: false },
  focusAtEnd: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:modelValue',
  'update:pendingFields',
  'submit',
  'cancel',
  'blur',
]);

const { t } = useI18n();
const agents = useMapGetter('agents/getAgents');
const labels = useMapGetter('labels/getLabels');

const { isLoadingContacts, searchContacts, getDropdownOptions } =
  useSmartTextParser({ agents, labels });

const editorRef = ref(null);
const chips = ref([]);
const pendingToken = ref(null);
const dropdownIndex = ref(0);
const userNavigated = ref(false);
const isFocused = ref(false);
const dropdownOptions = computed(() => getDropdownOptions(pendingToken.value));

onMounted(() => {
  if (props.modelValue && editorRef.value) {
    editorRef.value.textContent = props.modelValue;
  }
  if (props.focusAtEnd && editorRef.value) {
    editorRef.value.focus();
    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(editorRef.value);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  } else if (props.autofocus) {
    editorRef.value?.focus();
  }
});

watch(
  () => props.modelValue,
  val => {
    if (!editorRef.value) return;
    if (isFocused.value) return;
    if (chips.value.length > 0) return;
    if (editorRef.value.textContent !== val) {
      editorRef.value.textContent = val ?? '';
    }
  }
);

function emitUpdate() {
  const { title, description } = extractTitleAndDescription(editorRef.value);
  emit('update:modelValue', title);
  emit('update:pendingFields', derivePendingFields(chips.value, description));
}

// Guarda a posição do cursor a cada digitação: o clique numa opção do
// dropdown pode alterar a seleção antes da confirmação ser aplicada
let savedCaret = null;

function saveCaret() {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return;
  const range = sel.getRangeAt(0);
  if (!editorRef.value?.contains(range.startContainer)) return;
  savedCaret = { node: range.startContainer, offset: range.startOffset };
}

function restoreCaret() {
  if (!savedCaret || !editorRef.value?.contains(savedCaret.node)) return;
  const range = document.createRange();
  range.setStart(savedCaret.node, savedCaret.offset);
  range.collapse(true);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
}

function onInput() {
  pendingToken.value = detectPendingToken(editorRef.value);
  saveCaret();
  dropdownIndex.value = 0;
  userNavigated.value = false;
  if (pendingToken.value?.type === 'contact') {
    searchContacts(pendingToken.value.query);
  }
  emitUpdate();
}

function confirmDescriptionMode() {
  const type = pendingToken.value?.type;
  if (type !== 'description' && type !== 'descriptionEnd') return;
  // O clique no dropdown pode ter deslocado a seleção; restaura a posição
  // capturada quando a barra foi digitada antes de aplicar o token
  restoreCaret();
  confirmToken(editorRef.value, pendingToken.value, null, chips);
  pendingToken.value = null;
  dropdownIndex.value = 0;
  userNavigated.value = false;
  emitUpdate();
}

function selectOption(option) {
  if (!pendingToken.value) return;
  restoreCaret();
  confirmToken(editorRef.value, pendingToken.value, option, chips);
  pendingToken.value = null;
  dropdownIndex.value = 0;
  userNavigated.value = false;
  emitUpdate();
}

function closeDropdown() {
  pendingToken.value = null;
  dropdownIndex.value = 0;
  userNavigated.value = false;
}

function onKeydown(e) {
  const hasDropdown = dropdownOptions.value.length > 0 && pendingToken.value;

  if (hasDropdown && e.key === 'ArrowDown') {
    e.preventDefault();
    dropdownIndex.value = Math.min(
      dropdownIndex.value + 1,
      dropdownOptions.value.length - 1
    );
    userNavigated.value = true;
    return;
  }
  if (hasDropdown && e.key === 'ArrowUp') {
    e.preventDefault();
    dropdownIndex.value = Math.max(dropdownIndex.value - 1, 0);
    userNavigated.value = true;
    return;
  }

  if (e.key === 'Enter') {
    e.preventDefault();
    if (
      (pendingToken.value?.type === 'date' && pendingToken.value.parsed) ||
      pendingToken.value?.type === 'time-only' ||
      pendingToken.value?.type === 'description' ||
      pendingToken.value?.type === 'descriptionEnd' ||
      (pendingToken.value?.type === 'recurrence' && pendingToken.value.parsed)
    ) {
      confirmToken(editorRef.value, pendingToken.value, null, chips);
      pendingToken.value = null;
      userNavigated.value = false;
      emitUpdate();
    } else if (hasDropdown) {
      selectOption(dropdownOptions.value[dropdownIndex.value]);
    } else {
      emit('submit');
    }
    return;
  }

  if (e.key === ' ') {
    if (
      (pendingToken.value?.type === 'date' &&
        pendingToken.value.parsed?.due_date) ||
      pendingToken.value?.type === 'time-only' ||
      (pendingToken.value?.type === 'recurrence' && pendingToken.value.parsed)
    ) {
      e.preventDefault();
      confirmToken(editorRef.value, pendingToken.value, null, chips);
      pendingToken.value = null;
      userNavigated.value = false;
      emitUpdate();
      return;
    }
    if (hasDropdown && userNavigated.value) {
      e.preventDefault();
      selectOption(dropdownOptions.value[dropdownIndex.value]);
      return;
    }
    // Espaço sem navegar: fecha o dropdown e deixa o caractere ser digitado normalmente
    if (hasDropdown) {
      closeDropdown();
    }
  }

  if (e.key === 'Escape') {
    if (pendingToken.value) {
      closeDropdown();
    } else {
      emit('cancel');
    }
    return;
  }

  if (e.key === 'Backspace') {
    const removed = removeLastChip(editorRef.value, chips);
    if (removed) {
      e.preventDefault();
      pendingToken.value = null;
      emitUpdate();
    }
  }
}

function onFocus() {
  isFocused.value = true;
}

function onBlur() {
  isFocused.value = false;
  setTimeout(closeDropdown, 150);
  emit('blur');
}

function onPaste(e) {
  e.preventDefault();
  const text = e.clipboardData.getData('text/plain');
  document.execCommand('insertText', false, text);
}

defineExpose({ focus: () => editorRef.value?.focus() });
</script>

<template>
  <div class="relative w-full">
    <div
      ref="editorRef"
      contenteditable="true"
      class="smart-title-editor w-full bg-transparent outline-none min-h-[1.5rem] break-words"
      :data-placeholder="placeholder"
      @input="onInput"
      @keydown="onKeydown"
      @focus="onFocus"
      @blur="onBlur"
      @paste="onPaste"
    />

    <div
      v-if="
        pendingToken &&
        (dropdownOptions.length > 0 ||
          pendingToken.type === 'contact' ||
          pendingToken.type === 'label' ||
          pendingToken.type === 'description' ||
          pendingToken.type === 'descriptionEnd')
      "
      class="absolute left-0 top-full mt-1 z-40 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 min-w-[180px] max-h-80 overflow-y-auto"
      @mousedown.prevent
    >
      <!-- Prioridade -->
      <template v-if="pendingToken.type === 'priority'">
        <button
          v-for="(opt, idx) in dropdownOptions"
          :key="opt.key"
          type="button"
          class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors"
          :class="
            idx === dropdownIndex
              ? 'bg-n-alpha-1 text-n-brand font-medium'
              : 'text-n-slate-12 hover:bg-n-alpha-1'
          "
          @mousedown.prevent="selectOption(opt)"
        >
          <span
            class="i-lucide-flag size-3.5 flex-shrink-0"
            :class="opt.flag"
          />
          {{ opt.label }}
        </button>
      </template>

      <!-- Responsável -->
      <template v-else-if="pendingToken.type === 'assignee'">
        <button
          v-for="(agent, idx) in dropdownOptions"
          :key="agent.id"
          type="button"
          class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors"
          :class="
            idx === dropdownIndex
              ? 'bg-n-alpha-1 text-n-brand font-medium'
              : 'text-n-slate-12 hover:bg-n-alpha-1'
          "
          @mousedown.prevent="selectOption(agent)"
        >
          <div
            class="size-5 rounded-full overflow-hidden flex-shrink-0 bg-n-surface-2 flex items-center justify-center"
          >
            <img
              v-if="agent.thumbnail"
              :src="agent.thumbnail"
              class="size-full object-cover"
            />
            <span
              v-else
              class="agent-avatar-fallback text-[9px] font-bold text-white size-full flex items-center justify-center"
            >
              {{ agent.name.slice(0, 2).toUpperCase() }}
            </span>
          </div>
          <span class="truncate">{{ agent.name }}</span>
        </button>
      </template>

      <!-- Labels -->
      <template v-else-if="pendingToken.type === 'label'">
        <button
          v-for="(lbl, idx) in dropdownOptions"
          :key="lbl.id"
          type="button"
          class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors"
          :class="
            idx === dropdownIndex
              ? 'bg-n-alpha-1 text-n-brand font-medium'
              : 'text-n-slate-12 hover:bg-n-alpha-1'
          "
          @mousedown.prevent="selectOption(lbl)"
        >
          <span
            class="size-2.5 rounded-full flex-shrink-0"
            :style="{ backgroundColor: lbl.color }"
          />
          <span class="truncate">{{ lbl.title }}</span>
        </button>
        <p
          v-if="dropdownOptions.length === 0"
          class="px-3 py-2 text-sm text-n-slate-10 italic"
        >
          {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
        </p>
      </template>

      <!-- Contato -->
      <template v-else-if="pendingToken.type === 'contact'">
        <p
          v-if="!pendingToken.query"
          class="px-3 py-2 text-sm text-n-slate-10 italic"
        >
          {{ t('TASKS.SMART_INPUT.TYPE_TO_SEARCH') }}
        </p>
        <p
          v-else-if="isLoadingContacts && dropdownOptions.length === 0"
          class="px-3 py-2 text-sm text-n-slate-10 italic"
        >
          {{ t('TASKS.SMART_INPUT.SEARCHING') }}
        </p>
        <p
          v-else-if="
            !isLoadingContacts &&
            pendingToken.query &&
            dropdownOptions.length === 0
          "
          class="px-3 py-2 text-sm text-n-slate-10 italic"
        >
          {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
        </p>
        <button
          v-for="(c, idx) in dropdownOptions"
          :key="c.id"
          type="button"
          class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors"
          :class="
            idx === dropdownIndex
              ? 'bg-n-alpha-1 text-n-brand font-medium'
              : 'text-n-slate-12 hover:bg-n-alpha-1'
          "
          @mousedown.prevent="selectOption(c)"
        >
          <span class="i-lucide-user size-3 flex-shrink-0 text-n-slate-9" />
          <span class="truncate">{{ c.name }}</span>
          <span
            v-if="c.company_name"
            class="text-n-slate-9 text-xs ml-1 truncate"
          >
            {{ c.company_name }}
          </span>
        </button>
      </template>

      <!-- Descrição: iniciar ou finalizar -->
      <template
        v-else-if="
          pendingToken.type === 'description' ||
          pendingToken.type === 'descriptionEnd'
        "
      >
        <button
          type="button"
          class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors bg-n-alpha-1 text-n-brand font-medium"
          @mousedown.prevent="confirmDescriptionMode"
        >
          <span
            class="size-3.5 flex-shrink-0"
            :class="
              pendingToken.type === 'descriptionEnd'
                ? 'i-lucide-corner-down-left'
                : 'i-lucide-align-left'
            "
          />
          {{
            pendingToken.type === 'descriptionEnd'
              ? t('TASKS.SMART_INPUT.DESCRIPTION_END')
              : t('TASKS.SMART_INPUT.DESCRIPTION')
          }}
        </button>
      </template>

      <!-- Tipo de tarefa -->
      <template v-else-if="pendingToken.type === 'taskType'">
        <button
          v-for="(tp, idx) in dropdownOptions"
          :key="tp.key"
          type="button"
          class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors"
          :class="
            idx === dropdownIndex
              ? 'bg-n-alpha-1 text-n-brand font-medium'
              : 'text-n-slate-12 hover:bg-n-alpha-1'
          "
          @mousedown.prevent="selectOption(tp)"
        >
          <span
            class="size-3.5 flex-shrink-0 text-n-slate-9"
            :class="[tp.icon]"
          />
          <span class="truncate">{{ tp.label }}</span>
        </button>
      </template>
    </div>
  </div>
</template>

<style>
.smart-title-editor:empty::before {
  content: attr(data-placeholder);
  color: var(--n-slate-10, #9ba3af);
  font-weight: 400;
  pointer-events: none;
}

.agent-avatar-fallback {
  background-color: #4f87f0;
}
</style>
